if (!customElements.get('hero-video-banner')) {
  // How long we give the active video to become playable before giving up
  // and hiding the whole section, letting whatever comes after it (e.g. a
  // slideshow) take its place instead of stacking a static poster on top
  // of another static image.
  const LOAD_TIMEOUT_MS = 6000;

  class HeroVideoBanner extends HTMLElement {
    constructor() {
      super();
      this.videos = Array.from(this.querySelectorAll('.hero-video-banner__video'));
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.breakpointQuery = window.matchMedia('(min-width: 750px)');
    }

    connectedCallback() {
      if (this.reducedMotionQuery.matches) {
        // Respect the OS-level preference: never attempt video at all,
        // and there's nothing to time out, so just leave the poster.
        return;
      }

      this.videos.forEach((video) => this.setupVideo(video));

      // <source media="..."> is only evaluated once, on initial load.
      // If the viewport crosses the mobile/desktop breakpoint (window
      // resize, or a phone/tablet rotating), reload so the correct
      // source is selected again.
      this.breakpointQuery.addEventListener('change', () => this.reloadActiveVideo());
    }

    isActive(video) {
      return video.offsetParent !== null;
    }

    setupVideo(video) {
      let settled = false;

      const markReady = () => {
        settled = true;
      };
      video.addEventListener('loadeddata', markReady, { once: true });
      video.addEventListener('error', () => this.handleFailure(video), { once: true });

      this.playVideo(video);

      window.setTimeout(() => {
        // readyState >= 2 (HAVE_CURRENT_DATA) means it already has a
        // usable frame even if the 'loadeddata' event fired before we
        // attached the listener.
        if (!settled && video.readyState < 2) {
          this.handleFailure(video);
        }
      }, LOAD_TIMEOUT_MS);
    }

    handleFailure(video) {
      // Only act if this is the video currently relevant to the viewport.
      // A timed-out video for the "other" breakpoint shouldn't hide a
      // perfectly fine one that's actually on screen.
      if (!this.isActive(video)) return;
      this.classList.add('hero-video-banner--failed');
    }

    playVideo(video) {
      const playPromise = video.play();
      // Safari/iOS sometimes need an explicit .play() call rather than
      // relying on the autoplay attribute alone.
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    reloadActiveVideo() {
      const activeVideo = this.videos.find((video) => this.isActive(video));
      if (!activeVideo) return;
      // Give it another chance on the new breakpoint rather than staying
      // hidden because the previous breakpoint's video failed.
      this.classList.remove('hero-video-banner--failed');
      activeVideo.load();
      this.setupVideo(activeVideo);
    }
  }

  customElements.define('hero-video-banner', HeroVideoBanner);
}